// src/contexts/ChatContext.jsx
import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Chat context for persistent chat state across navigation
const ChatContext = createContext();

// Action types
const CHAT_ACTIONS = {
  SET_MESSAGES: 'SET_MESSAGES',
  ADD_MESSAGE: 'ADD_MESSAGE',
  CLEAR_CHAT: 'CLEAR_CHAT',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  SET_WORKSPACE_CONTEXT: 'SET_WORKSPACE_CONTEXT'
};

// Initial state
const initialState = {
  // Chat state organized by workspaceId-agentId key
  chats: {},
  // Current active chat context
  activeChat: null,
  // Global loading and error states
  isLoading: false,
  error: null
};

// Reducer function
const chatReducer = (state, action) => {
  switch (action.type) {
    case CHAT_ACTIONS.SET_WORKSPACE_CONTEXT:
      return {
        ...state,
        activeChat: action.payload
      };

    case CHAT_ACTIONS.SET_MESSAGES:
      const { workspaceId, agentId, messages } = action.payload;
      const chatKey = `${workspaceId}-${agentId}`;
      return {
        ...state,
        chats: {
          ...state.chats,
          [chatKey]: {
            ...state.chats[chatKey],
            messages: messages,
            lastUpdated: Date.now()
          }
        }
      };

    case CHAT_ACTIONS.ADD_MESSAGE:
      const { workspaceId: wsId, agentId: agId, message } = action.payload;
      const key = `${wsId}-${agId}`;
      const existingChat = state.chats[key] || { messages: [], lastUpdated: Date.now() };
      
      return {
        ...state,
        chats: {
          ...state.chats,
          [key]: {
            ...existingChat,
            messages: [...existingChat.messages, message],
            lastUpdated: Date.now()
          }
        }
      };

    case CHAT_ACTIONS.UPDATE_MESSAGE:
      const { workspaceId: wsId2, agentId: agId2, messageId, updates } = action.payload;
      const chatKey2 = `${wsId2}-${agId2}`;
      const chat = state.chats[chatKey2];
      
      if (!chat) return state;
      
      const updatedMessages = chat.messages.map(msg => 
        msg.id === messageId ? { ...msg, ...updates } : msg
      );
      
      return {
        ...state,
        chats: {
          ...state.chats,
          [chatKey2]: {
            ...chat,
            messages: updatedMessages,
            lastUpdated: Date.now()
          }
        }
      };

    case CHAT_ACTIONS.CLEAR_CHAT:
      const { workspaceId: wsId3, agentId: agId3 } = action.payload;
      const chatKey3 = `${wsId3}-${agId3}`;
      return {
        ...state,
        chats: {
          ...state.chats,
          [chatKey3]: {
            messages: [],
            lastUpdated: Date.now()
          }
        }
      };

    case CHAT_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case CHAT_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };

    default:
      return state;
  }
};

// Provider component
export const ChatProvider = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);

  // Set active chat context
  const setActiveChat = useCallback((workspaceId, agentId) => {
    dispatch({
      type: CHAT_ACTIONS.SET_WORKSPACE_CONTEXT,
      payload: { workspaceId, agentId }
    });
  }, []);

  // Get messages for a specific chat
  const getMessages = useCallback((workspaceId, agentId) => {
    const chatKey = `${workspaceId}-${agentId}`;
    return state.chats[chatKey]?.messages || [];
  }, [state.chats]);

  // Set messages for a specific chat
  const setMessages = useCallback((workspaceId, agentId, messages) => {
    dispatch({
      type: CHAT_ACTIONS.SET_MESSAGES,
      payload: { workspaceId, agentId, messages }
    });
  }, []);

  // Add a message to a specific chat
  const addMessage = useCallback((workspaceId, agentId, message) => {
    dispatch({
      type: CHAT_ACTIONS.ADD_MESSAGE,
      payload: { workspaceId, agentId, message }
    });
  }, []);

  // Update a specific message
  const updateMessage = useCallback((workspaceId, agentId, messageId, updates) => {
    dispatch({
      type: CHAT_ACTIONS.UPDATE_MESSAGE,
      payload: { workspaceId, agentId, messageId, updates }
    });
  }, []);

  // Clear a specific chat
  const clearChat = useCallback((workspaceId, agentId) => {
    dispatch({
      type: CHAT_ACTIONS.CLEAR_CHAT,
      payload: { workspaceId, agentId }
    });
  }, []);

  // Set global loading state
  const setLoading = useCallback((loading) => {
    dispatch({
      type: CHAT_ACTIONS.SET_LOADING,
      payload: loading
    });
  }, []);

  // Set global error state
  const setError = useCallback((error) => {
    dispatch({
      type: CHAT_ACTIONS.SET_ERROR,
      payload: error
    });
  }, []);

  // Get chat metadata
  const getChatMetadata = useCallback((workspaceId, agentId) => {
    const chatKey = `${workspaceId}-${agentId}`;
    const chat = state.chats[chatKey];
    return chat ? {
      messageCount: chat.messages.length,
      lastUpdated: chat.lastUpdated,
      hasMessages: chat.messages.length > 0
    } : {
      messageCount: 0,
      lastUpdated: null,
      hasMessages: false
    };
  }, [state.chats]);

  // Clear all chats (useful for logout)
  const clearAllChats = useCallback(() => {
    dispatch({
      type: CHAT_ACTIONS.CLEAR_CHAT,
      payload: { workspaceId: '*', agentId: '*' }
    });
  }, []);

  const value = {
    // State
    chats: state.chats,
    activeChat: state.activeChat,
    isLoading: state.isLoading,
    error: state.error,
    
    // Actions
    setActiveChat,
    getMessages,
    setMessages,
    addMessage,
    updateMessage,
    clearChat,
    setLoading,
    setError,
    getChatMetadata,
    clearAllChats
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

// Custom hook to use chat context
export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export default ChatContext;
